import { Tooltip } from "@mui/material";
import AlertTriangle from "assets/img/svg/alertTriangle";
import LastElementTable from "components/shared/LastElementTable";
import type { LastElementTableAPIData, LastElementTableColumns } from "components/shared/LastElementTable/type";
import ManagementTableAuthorComponent from "components/shared/ManagementTableAuthorComponent";
import ModalBase from "components/shared/ModalBase";
import type { IStatusRule } from "components/shared/StatusChip";
import StatusChip from "components/shared/StatusChip";
import type { WhiteDropdownMenuOptions } from "components/shared/WhiteDropdownMenu";
import { WhiteDropdownMenu } from "components/shared/WhiteDropdownMenu";
import { PortalType } from "contexts/portal.context";
import { formatLocaleDateString, formatNumber } from "helpers/utils";
import type { TPaidStatus } from "hooks/useAPIPagination";
import { useCompanySettings } from "hooks/useCompanySettings";
import { Fragment, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { IPayment } from "store/paymentSlice";
import type { MainPortalType } from "store/settingsStore";
import { useAppSelector } from "store/store";
import { EPortalType, PaymentStatus } from "store/types";

import { EPaymentTableStatus } from "..";
import PaymentUpdateModal from "../PaymentUpdateModal";
import { EPaymentBeneficiaryFilter } from "../types";
import { isPaidWithIBAN } from "../utils";

interface IPaymentTable {
    type: MainPortalType;
    /**
     * Query entered in search bar.
     */
    searchBarValue: string;
    handleChangeFilterValue: (e: React.ChangeEvent<HTMLInputElement>) => void;
    /**
     * Rules for the status chip. Once we homogenise partial payment, we can add this to component directly.
     */
    paymentStatusRules: Record<EPaymentTableStatus, IStatusRule>;
    /**
     * Show payment details drawer on click button "Voir détails" or on row
     */
    handleShowPaymentDetails: (payment: IPayment) => void;
    onlyWaitingPayments?: boolean;
    /**
     * Status for payment filter (paid/unpaid, etc.)
     */
    paymentStatusFilter?: TPaidStatus;
    APIData?: LastElementTableAPIData;
    beneficiaryFilter?: EPaymentBeneficiaryFilter;
}

export const bookPaymentsStatus = (payment: IPayment) => {
    if (payment.paidStatus === EPaymentTableStatus.CANCELLED) return EPaymentTableStatus.CANCELLED;
    const isPaid = payment.netRemainingToPay === 0;
    return isPaid ? EPaymentTableStatus.PAID : EPaymentTableStatus.TO_PAY;
};

export const getPaidStatus = (status: TPaidStatus) => {
    switch (status) {
        case "paid":
            return PaymentStatus.PAID;
        case "topay":
            return PaymentStatus.TO_PAY;
        case "unpaid":
            return PaymentStatus.NOT_PAID;
    }
};

/**
 * Table with payments for both book and press/audiovisual companies.
 */
const PaymentTable = ({
    type,
    searchBarValue,
    paymentStatusRules,
    handleChangeFilterValue,
    handleShowPaymentDetails,
    onlyWaitingPayments = false,
    paymentStatusFilter,
    beneficiaryFilter,
    APIData,
}: IPaymentTable) => {
    const [t] = useTranslation();

    const { paymentsStatus, paginatedPayments, paginatedPaymentsStatus } = useAppSelector((state) => state.payment);
    const { getCompanySettings } = useCompanySettings();
    const usesBeneficiaryProfileCompleteCheck = getCompanySettings("uses_beneficiary_profile_complete_check");

    const [currentPayment, setCurrentPayment] = useState<IPayment | null>();
    const [modalType, setModalType] = useState("");

    const portalType = useContext(PortalType);
    const isBookCompany = portalType === EPortalType.BOOK;

    const filteredPayments = useMemo(() => {
        const filterValue = searchBarValue.replace(/ /g, "");
        let filtered = paginatedPayments.filter(
            (item) =>
                item.name.replace(/ /g, "")?.toLowerCase().includes(filterValue.toLowerCase()) ||
                `${item.beneficiary.name ?? ""}${item.beneficiary.surname ?? ""}`
                    .toLowerCase()
                    .includes(filterValue.toLowerCase()) ||
                `${item.beneficiary.surname ?? ""}${item.beneficiary.name ?? ""}`
                    .toLowerCase()
                    .includes(filterValue.toLowerCase()),
        );

        if (paymentStatusFilter && paymentStatusFilter !== "all") {
            filtered = filtered.filter((item) => item.paidStatus === getPaidStatus(paymentStatusFilter));
        }

        if (beneficiaryFilter && beneficiaryFilter === EPaymentBeneficiaryFilter.COMPLETED) {
            filtered = filtered.filter((item) => item.beneficiary?.isProfileComplete === 1);
        }

        return filtered.reverse();
    }, [searchBarValue, paymentStatusFilter, paginatedPayments, beneficiaryFilter]);

    /**
     * Used to verify and display payment types within the column "Types of payment"
     **/
    const formatPaymentTypes = (payment: IPayment | undefined) => {
        const chosenMethods = [];
        if (payment?.iban && payment?.iban !== "") chosenMethods.push(t("Payment.RIB"));
        if (payment?.paypalEmail && payment?.paypalEmail !== "") chosenMethods.push(t("Payment.Paypal"));
        if (payment?.bankCheck && payment?.bankCheck === 1) chosenMethods.push(t("Payment.Check"));
        return chosenMethods.join(", ");
    };

    const pressPaymentsStatus = (payment: IPayment) => {
        const isPartialPayment = payment.netRemainingToPay > 0 && payment?.netValue != payment.netRemainingToPay;
        const isPaid = payment.netRemainingToPay === 0;

        return isPaid
            ? EPaymentTableStatus.PAID
            : isPartialPayment
              ? EPaymentTableStatus.PARTIALLY_PAID
              : EPaymentTableStatus.TO_PAY;
    };

    const menuOptions: WhiteDropdownMenuOptions[] = [
        {
            id: 1,
            name: t("PartialPayment.Delete"),
            action: () => setModalType("update"),
            trackedEvent: "payments-delete",
        },
    ];

    const paymentColumns: LastElementTableColumns[] = [
        {
            id: "name",
            label: t("Payment.Author"),
            align: "left",
            sort: true,
            avatar: false,
            maxWidth: "220px",
            minWidth: isBookCompany ? "220px" : "",
            format: (value: string, id?: number, payment?: IPayment) => (
                <ManagementTableAuthorComponent
                    author={value}
                    beneficiaryId={id}
                    avatarUrl={payment?.beneficiaryAvatarUrl}
                    isProfileIncomplete={
                        usesBeneficiaryProfileCompleteCheck ? payment?.beneficiary?.isProfileComplete === 0 : undefined
                    }
                    handleQuery={handleChangeFilterValue}
                    page="payment"
                    maxWidth="200px !important"
                    nameStyle={{ overflow: "hidden", textOverflow: "ellipsis" }}
                />
            ),
        },
        {
            id: "netValue",
            label: t("Payment.Net"),
            align: "right",
            maxWidth: "230px",
            minWidth: isBookCompany ? "230px" : "",
            format: (value: number) => (
                <span style={{ maxWidth: "210px" }}>
                    {formatNumber(value / 100, { isCurrency: true, always2Digit: true })}
                </span>
            ),
            sort: true,
        },
        {
            id: "netRemainingToPay",
            label: t("Payment.NetRemainingToPay"),
            hidden: type === EPortalType.BOOK,
            align: "right",
            format: (value: number) => (
                <span>{formatNumber(value / 100, { isCurrency: true, always2Digit: true })}</span>
            ),
            sort: true,
        },
        {
            id: type === EPortalType.BOOK ? "status" : "paidStatus",
            label: t("Payment.PaymentStatus"),
            align: "left",
            format: (_, __, payment: IPayment) => {
                const tooltipText =
                    payment.netRemainingToPay > 0 && type !== EPortalType.BOOK
                        ? `${t("Payment.RemainingToPayTooltip1")}${formatNumber(payment.netRemainingToPay / 100, {
                              isCurrency: true,
                              always2Digit: true,
                          })}${t("Payment.RemainingToPayTooltip2")}`
                        : "";
                const isBankInfoIncomplete = payment?.bankInfoComplete === 0;
                const isForeignBeneficiary =
                    payment?.beneficiary?.bankingStatus === "foreign_local" ||
                    payment?.beneficiary?.bankingStatus === "foreign_international";
                const showInvalidBankInfoWarning =
                    !isBookCompany &&
                    !!payment &&
                    isBankInfoIncomplete &&
                    !isForeignBeneficiary &&
                    isPaidWithIBAN(payment);
                return (
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                            justifyContent: "space-between",
                            maxWidth: "176px",
                        }}
                        key={`status-${payment?.id}`}
                    >
                        <StatusChip
                            statusRules={paymentStatusRules}
                            status={
                                type === EPortalType.BOOK ? bookPaymentsStatus(payment) : pressPaymentsStatus(payment)
                            }
                            tooltipText={tooltipText}
                        />
                        {showInvalidBankInfoWarning && (
                            <Tooltip title={t("Payment.WrongBankInfo")} arrow>
                                <span style={{ height: "1.25rem" }}>
                                    <AlertTriangle height={20} width={20} />
                                </span>
                            </Tooltip>
                        )}
                    </div>
                );
            },
            sort: isBookCompany ? false : true,
        },
        {
            id: "date",
            label: t("PartialPayment.LotsTable.PaymentDate"),
            align: "right",
            format: (_, __, payment: IPayment) => (
                <span>
                    {bookPaymentsStatus(payment) === EPaymentTableStatus.PAID
                        ? formatLocaleDateString({
                              date: payment?.date ?? new Date(),
                              options: { numeric: true, timeZone: "UTC" },
                          })
                        : "-"}
                </span>
            ),
        },
        {
            id: "paymentLotId",
            label: t("PartialPayment.LotsTable.PaymentLot"),
            hidden: type !== EPortalType.BOOK,
            align: "right",
            format: (value: number) => <span>#{value}</span>,
            sort: true,
        },
        {
            id: "acceptedPayments",
            label: t("Payment.PaymentTypes"),
            align: "left",
            hidden: isBookCompany && onlyWaitingPayments,
            format: (_, __, payment?: IPayment) => (
                <span>
                    {formatPaymentTypes(payment) === "" ? t("Payment.NotSpecified") : formatPaymentTypes(payment)}
                </span>
            ),
        },
        {
            id: "id",
            label: "",
            align: "right",
            stopPropagation: true,
            format: (_, __, payment: IPayment) =>
                !isBookCompany ? (
                    <button className={"btn-detail"} onClick={() => handleShowPaymentDetails(payment)}>
                        {t("Payment.SeeStatement")}
                    </button>
                ) : (
                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                        <WhiteDropdownMenu
                            options={menuOptions}
                            buttonName={t("PartialPayment.Actions.SeeDetails")}
                            mainAction={() => handleShowPaymentDetails(payment)}
                            onMenuClick={() => setCurrentPayment(payment)}
                        />
                    </div>
                ),
            sort: false,
        },
    ];

    const isLoading = useMemo(() => {
        return ["idle", "pending"].includes(
            onlyWaitingPayments || !isBookCompany ? paymentsStatus : paginatedPaymentsStatus,
        );
    }, [paymentsStatus, paginatedPaymentsStatus]);

    const columnNumbers = () => {
        if (type === EPortalType.BOOK) {
            return onlyWaitingPayments ? 6 : 7;
        } else {
            return 7;
        }
    };

    return (
        <Fragment>
            <ModalBase
                open={modalType === "update"}
                onClose={() => setModalType("")}
                modalTitle={t("PartialPayment.PaymentPage.UpdatePayment")}
                modalSubtitle={t("PartialPayment.PaymentPage.ModalSubtitle")}
            >
                <PaymentUpdateModal
                    type="payment"
                    paymentLotId={currentPayment?.paymentLotId}
                    beneficiaryId={currentPayment?.beneficiary.id}
                    handleClose={() => setModalType("")}
                    updateType="erase"
                    context="payments-batch-correct"
                />
            </ModalBase>
            <LastElementTable
                columns={paymentColumns}
                data={!isBookCompany ? filteredPayments : paginatedPayments}
                onRowClick={(_, __, ___, row) => handleShowPaymentDetails(row)}
                onRowClickId="id"
                max={-1}
                dataType="payment"
                stickyFooter
                isLoading={isLoading}
                columnsNumber={columnNumbers()}
                APIData={APIData}
                dataCount={APIData ? APIData.count : undefined}
            />
        </Fragment>
    );
};

export default PaymentTable;
